import React, { createContext, useContext, useState, useEffect } from 'react';
import { superAdminApi } from '../services/superAdminApi';

const SuperAdminAuthContext = createContext({});

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

export const SuperAdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Token storage keys
  const TOKEN_KEY = 'super_admin_token';
  const USER_KEY = 'super_admin_user';

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    console.log('Initializing Super Admin Auth...');
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      console.log('Stored token exists:', !!storedToken);
      console.log('Stored user exists:', !!storedUser);

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);

        // Verify token is still valid
        try {
          console.log('Verifying stored token...');
          const response = await superAdminApi.getProfile();
          setUser(response.data.user);
          console.log('Token verified successfully');
        } catch (error) {
          console.log('Token verification failed:', error.message);
          // Token is invalid, clear auth state
          await logout();
        }
      } else {
        console.log('No stored credentials found');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      await logout();
    } finally {
      console.log('Auth initialization complete, isLoading set to false');
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      console.log('Login attempt with credentials:', { email: credentials.email, password: '***' });
      console.log('API Base URL:', import.meta.env.VITE_API_BASE_URL);
      
      const response = await superAdminApi.login(credentials);
      console.log('Login response:', response);
      
      const { access_token, user: userData } = response.data;

      // Store token and user data
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Update state
      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', error.response || error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await superAdminApi.logout();
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear storage and state regardless of API call success
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await superAdminApi.refreshToken();
      const { access_token } = response.data;

      localStorage.setItem(TOKEN_KEY, access_token);
      setToken(access_token);

      return access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      await logout();
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await superAdminApi.updateProfile(profileData);
      const updatedUser = response.data.user;

      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      setUser(updatedUser);

      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const checkPermission = (permission) => {
    if (!isAuthenticated || !user) {
      return false;
    }
    
    // Super admins have all permissions
    return true;
  };

  const getAuthHeader = () => {
    return token ? `Bearer ${token}` : null;
  };

  const contextValue = {
    // State
    user,
    isAuthenticated,
    isLoading,
    token,

    // Actions
    login,
    logout,
    refreshToken,
    updateProfile,
    checkPermission,
    getAuthHeader,

    // Utilities
    isLoggedIn: isAuthenticated,
    hasPermission: checkPermission,
  };

  return (
    <SuperAdminAuthContext.Provider value={contextValue}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};